import React, { useEffect, useState } from "react";
import { Building2, Users, Baby, DoorOpen, Wallet, AlertTriangle, Radio, UserPlus, Mail, Activity, Copy } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Facility owner "baby dashboard" — facility-scoped onboarding, staff adds, monitoring and private analytics.
// Platform-wide monitoring/override stays on the Master dashboard.
export default function FacilityDash({ currentUser, onHome }) {
  const [ov, setOv] = useState(null);
  const [invite, setInvite] = useState({ name: "", email: "", role: "staff" });
  const [busy, setBusy] = useState(false);
  const [claimLink, setClaimLink] = useState("");

  const load = () =>
    fetch(`${API}/api/facility/overview`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setOv(d))
      .catch(() => {});

  useEffect(() => { load(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    setBusy(true);
    setClaimLink("");
    try {
      const res = await fetch(`${API}/api/workspace/invite-member`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: invite.name, email: invite.email, role: invite.role }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Invite failed");
      toast.success(`Invite sent to ${invite.email}`);
      if (body.claim_link) setClaimLink(body.claim_link);
      setInvite({ name: "", email: "", role: invite.role });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 bg-white border border-[#FCE6E9] rounded-2xl text-sm font-medium focus:outline-none focus:border-[#E5A0BC] transition-colors";
  const stat = (icon, label, value, testid) => (
    <div className="bg-white border border-[#FCE6E9] rounded-3xl p-5 shadow-sm" data-testid={testid}>
      <span className="w-9 h-9 rounded-full bg-[#FCE6E9] flex items-center justify-center mb-3">{icon}</span>
      <p className="text-2xl font-black font-heading text-[#374151]">{value}</p>
      <p className="text-[10px] font-extrabold uppercase tracking-wider text-[#9CA3AF] mt-0.5">{label}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FFF9F2] pb-16" data-testid="facility-dash">
      <header className="bg-white border-b border-[#FCE6E9] px-4 sm:px-8 py-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#E5A0BC]">Facility Dashboard</p>
          <h1 className="text-xl font-black font-heading text-[#374151]" data-testid="facility-dash-name">{ov?.facilityName || "Your daycare"}</h1>
          <p className="text-[11px] text-[#9CA3AF] font-semibold">{currentUser?.fullName || currentUser?.email} — owner workspace. Platform-wide monitoring stays with Master Control.</p>
        </div>
        <button onClick={onHome} className="px-4 py-2 bg-[#FCE6E9] hover:bg-[#F8C8DC] text-[#B04A6B] rounded-full text-xs font-extrabold transition-colors" data-testid="facility-dash-home">
          Back to site
        </button>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="facility-dash-stats">
          {stat(<Users className="w-4 h-4 text-[#E5A0BC]" />, "Staff", ov?.counts?.staff ?? "—", "fd-staff")}
          {stat(<Baby className="w-4 h-4 text-[#E5A0BC]" />, "Children enrolled", ov?.counts?.children ?? "—", "fd-children")}
          {stat(<Users className="w-4 h-4 text-[#5CB3DC]" />, "Parents", ov?.counts?.parents ?? "—", "fd-parents")}
          {stat(<DoorOpen className="w-4 h-4 text-[#5CB3DC]" />, "Rooms", ov?.counts?.rooms ?? "—", "fd-rooms")}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E6F2FA] rounded-3xl p-6 shadow-sm" data-testid="facility-dash-analytics">
            <p className="text-xs font-extrabold uppercase tracking-wider text-[#5CB3DC] mb-4 flex items-center gap-1.5"><Wallet className="w-4 h-4" /> Private analytics</p>
            <div className="space-y-2.5 text-sm font-bold text-[#374151]">
              <p className="flex justify-between"><span className="text-[#9CA3AF] font-semibold">Invoiced total</span><span data-testid="fd-invoice-total">${(ov?.invoices?.total ?? 0).toFixed(2)}</span></p>
              <p className="flex justify-between"><span className="text-[#9CA3AF] font-semibold">Unpaid invoices</span><span data-testid="fd-unpaid">{ov?.invoices?.unpaid ?? 0}</span></p>
              <p className="flex justify-between"><span className="text-[#9CA3AF] font-semibold">Open complaints</span><span data-testid="fd-complaints">{ov?.open_complaints ?? 0}</span></p>
              <p className="flex justify-between"><span className="text-[#9CA3AF] font-semibold">Live events on air</span><span data-testid="fd-live">{ov?.active_live ?? 0}</span></p>
            </div>
          </div>

          <div className="bg-white border border-[#FCE6E9] rounded-3xl p-6 shadow-sm" data-testid="facility-dash-invite">
            <p className="text-xs font-extrabold uppercase tracking-wider text-[#E5A0BC] mb-4 flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Add staff / parents</p>
            <form onSubmit={sendInvite} className="space-y-2.5">
              <input required value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="fd-invite-name" />
              <input required type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="fd-invite-email" />
              <div className="flex gap-2">
                {["staff", "parent"].map((r) => (
                  <button type="button" key={r} onClick={() => setInvite({ ...invite, role: r })} data-testid={`fd-invite-role-${r}`} className={`flex-1 px-3 py-2 rounded-full text-xs font-extrabold capitalize transition-colors ${invite.role === r ? "bg-[#5CB3DC] text-white" : "bg-[#FFF9F2] border border-[#B3E0F2] text-[#3D7E9E]"}`}>
                    {r}
                  </button>
                ))}
              </div>
              <button type="submit" disabled={busy} className="w-full px-4 py-2.5 bg-[#374151] hover:bg-[#1F2937] text-white rounded-full text-xs font-extrabold transition-colors disabled:opacity-50" data-testid="fd-invite-submit">
                {busy ? "Sending…" : "Send invite"}
              </button>
            </form>
            {claimLink && (
              <button onClick={() => { navigator.clipboard.writeText(claimLink); toast.success("Claim link copied"); }} className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-[#FFF2D1] border border-[#F5E3B3] text-[#8a6d1a] rounded-xl text-[10px] font-extrabold" data-testid="fd-claim-link">
                <Copy className="w-3 h-3" /> Email slow? Copy the manual claim link
              </button>
            )}
          </div>

          <div className="bg-white border border-[#FCE6E9] rounded-3xl p-6 shadow-sm" data-testid="facility-dash-alerts">
            <p className="text-xs font-extrabold uppercase tracking-wider text-[#E5A0BC] mb-4 flex items-center gap-1.5"><Activity className="w-4 h-4" /> Recent activity</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(ov?.alerts || []).length === 0 && <p className="text-[11px] text-[#9CA3AF] font-semibold">No activity yet — invites and live events will show here.</p>}
              {(ov?.alerts || []).map((a) => (
                <p key={a.id} className="text-[11px] font-semibold text-[#374151] bg-[#FFF9F2] border border-[#FCE6E9] rounded-xl px-3 py-2" data-testid={`fd-alert-${a.id}`}>
                  {a.message}
                </p>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
